import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SlotCreateInput, SlotSegmentInput } from "@/types/slot";
import { format, addHours, setHours, setMinutes, addMinutes } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SlotFormProps {
  onSubmit: (data: SlotCreateInput) => void;
  initialDate?: Date;
  campaignId?: string;
  initialData?: Partial<SlotCreateInput>;
  isSimplifiedForm?: boolean;
  onProceedToCalendar?: (data: SlotCreateInput) => void;
}

export function SlotForm({
  onSubmit,
  initialDate = new Date(),
  campaignId,
  initialData,
  isSimplifiedForm = false,
  onProceedToCalendar,
}: SlotFormProps) {
  // Initialize segments
  const initialSegments: SlotSegmentInput[] = initialData?.segments || [];

  const [formData, setFormData] = useState<SlotCreateInput>({
    name: initialData?.name || "",
    capacity: initialData?.capacity || 10,
    description: initialData?.description || "",
    time_limit: initialData?.time_limit || 60,
    campaign_id: initialData?.campaign_id || campaignId,
    color: initialData?.color || "blue",
    tags: initialData?.tags || [],
    segments: initialSegments,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "capacity" || name === "time_limit" ? parseInt(value) : value,
    }));
  };

  const handleColorChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      color: value,
    }));
  };

  const handleSegmentDateChange = (index: number, date: Date | undefined) => {
    if (!date) return;

    const segments = [...formData.segments];
    const segment = segments[index];

    // Create new date objects to avoid mutating the original dates
    const currentStart = new Date(segment.start_time);
    const currentEnd = new Date(segment.end_time);

    // Keep the time from the current dates, but update the date
    const newStart = new Date(date);
    newStart.setHours(currentStart.getHours(), currentStart.getMinutes());

    const newEnd = new Date(date);
    newEnd.setHours(currentEnd.getHours(), currentEnd.getMinutes());

    segments[index] = {
      ...segment,
      start_time: newStart.toISOString(),
      end_time: newEnd.toISOString(),
    };

    setFormData((prev) => ({ ...prev, segments }));
  };

  const handleSegmentStartTimeChange = (index: number, timeString: string) => {
    const segments = [...formData.segments];
    const segment = segments[index];

    const [hours, minutes] = timeString.split(":").map(Number);
    const startDate = new Date(segment.start_time);
    startDate.setHours(hours, minutes);

    segments[index] = {
      ...segment,
      start_time: startDate.toISOString(),
    };

    setFormData((prev) => ({ ...prev, segments }));
  };

  const handleSegmentEndTimeChange = (index: number, timeString: string) => {
    const segments = [...formData.segments];
    const segment = segments[index];

    const [hours, minutes] = timeString.split(":").map(Number);
    const endDate = new Date(segment.end_time);
    endDate.setHours(hours, minutes);

    segments[index] = {
      ...segment,
      end_time: endDate.toISOString(),
    };

    setFormData((prev) => ({ ...prev, segments }));
  };

  const addTimeSegment = () => {
    // Add a new time segment that starts 1 hour after the last segment ends
    const lastSegment = formData.segments[formData.segments.length - 1];
    const newStartTime = lastSegment 
      ? addHours(new Date(lastSegment.end_time), 1)
      : new Date(initialDate);
    const newEndTime = addHours(newStartTime, 1);

    setFormData((prev) => ({
      ...prev,
      segments: [
        ...prev.segments,
        {
          start_time: newStartTime.toISOString(),
          end_time: newEndTime.toISOString(),
        },
      ],
    }));
  };

  const removeTimeSegment = (index: number) => {
    if (formData.segments.length <= 1) {
      // Don't remove the last segment
      return;
    }

    const segments = [...formData.segments];
    segments.splice(index, 1);

    setFormData((prev) => ({ ...prev, segments }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">
          Slot Name{" "}
          {isSimplifiedForm && <span className="text-danger">*</span>}
        </Label>
        <Input
          id="name"
          name="name"
          value={formData.name || ""}
          onChange={handleInputChange}
          placeholder="Enter a name for this slot"
          className={isSimplifiedForm ? "text-lg font-medium" : ""}
          required={isSimplifiedForm}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="time_limit">
          Slot Duration (minutes){" "}
          {isSimplifiedForm && <span className="text-danger">*</span>}
        </Label>
        <Input
          id="time_limit"
          name="time_limit"
          type="number"
          min="1"
          value={formData.time_limit || 60}
          onChange={handleInputChange}
          className={isSimplifiedForm ? "text-lg font-medium" : ""}
          required={isSimplifiedForm}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="capacity">
          Capacity {isSimplifiedForm && <span className="text-danger">*</span>}
        </Label>
        <Input
          id="capacity"
          name="capacity"
          type="number"
          min="1"
          value={formData.capacity || 10}
          onChange={handleInputChange}
          required={isSimplifiedForm}
        />
      </div>

      {!isSimplifiedForm && (
        <>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description || ""}
              onChange={handleInputChange}
              placeholder="Enter a description for this slot"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">Color</Label>
            <Select
              value={formData.color || "blue"}
              onValueChange={handleColorChange}
            >
              <SelectTrigger id="color">
                <SelectValue placeholder="Select color" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blue">Blue</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="red">Red</SelectItem>
                <SelectItem value="yellow">Yellow</SelectItem>
                <SelectItem value="purple">Purple</SelectItem>
                <SelectItem value="pink">Pink</SelectItem>
                <SelectItem value="orange">Orange</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <Label>Time Segments</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTimeSegment}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Add Segment
              </Button>
            </div>

            {formData.segments.map((segment, index) => {
              const segmentDate = new Date(segment.start_time);
              const startTime = format(new Date(segment.start_time), "HH:mm");
              const endTime = format(new Date(segment.end_time), "HH:mm");

              return (
                <div key={index} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium">Segment {index + 1}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTimeSegment(index)}
                      disabled={formData.segments.length <= 1}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !segmentDate && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {segmentDate
                            ? format(segmentDate, "PPP")
                            : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={segmentDate}
                          onSelect={(date) =>
                            handleSegmentDateChange(index, date)
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={startTime}
                        onChange={(e) =>
                          handleSegmentStartTimeChange(index, e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={endTime}
                        onChange={(e) =>
                          handleSegmentEndTimeChange(index, e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit">
          {isSimplifiedForm ? "Continue to Calendar" : "Create Slot"}
        </Button>
      </div>
    </form>
  );
}
